/**
 * reCAPTCHA v3 for the shop's pre-payment call to the HubSpot function's
 * `save-customer-stage` (SPHER-3026). That function refuses the call unless it carries a
 * valid token for the action `save_customer_stage`.
 *
 * Google's script is loaded lazily and once per page (`preloadRecaptcha` when the buyer
 * reaches the details step; otherwise on the first `getRecaptchaToken`). Every call to
 * `getRecaptchaToken` asks for a FRESH token: a token lasts two minutes and works once.
 *
 * This never blocks the checkout. Whenever there is no token — no site key in this build, the
 * script blocked or failing to load, `execute` throwing, or no answer within the timeout — it
 * resolves `null` and logs a console warning naming only the reason. It never rejects. The
 * call is then sent without a token, the function refuses it, and the purchase continues
 * exactly as it does when that call fails for any other reason.
 */
import config from '../config';

export const SAVE_CUSTOMER_STAGE_ACTION = 'save_customer_stage';
export const SCRIPT_ID = 'recaptcha-v3-script';
export const SCRIPT_URL = 'https://www.google.com/recaptcha/api.js?render=';
export const DEFAULT_TIMEOUT_MS = 5000;

const TIMED_OUT = Symbol('timed out');

// The one load of Google's script: { siteKey, promise } while it is loading or loaded.
let pending = null;

// Reasons only, never payload data: these warnings reach the buyer's console.
function warn(reason) {
  console.warn(`[reCAPTCHA] no token (${reason}); save-customer-stage is sent without one.`);
}

function removeScriptTag() {
  const tag = document.getElementById(SCRIPT_ID);
  if (tag) tag.remove();
}

/**
 * Loads api.js once and resolves `window.grecaptcha` when `grecaptcha.ready` fires (only then
 * is `execute` defined). A failed load is forgotten, so the next attempt injects it again.
 */
function loadGrecaptcha(siteKey) {
  if (pending && pending.siteKey === siteKey) return pending.promise;

  let script = null;
  const promise = new Promise((resolve, reject) => {
    const whenReady = () => {
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha || typeof grecaptcha.ready !== 'function') {
        reject(new Error('script_incomplete'));
        return;
      }
      grecaptcha.ready(() => resolve(window.grecaptcha));
    };

    if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
      whenReady();
      return;
    }

    removeScriptTag();
    script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL + encodeURIComponent(siteKey);
    script.async = true;
    script.addEventListener('load', whenReady, { once: true });
    script.addEventListener('error', () => reject(new Error('load_failed')), { once: true });
    document.head.appendChild(script);
  });

  pending = { siteKey, promise };
  promise.catch(() => {
    if (pending && pending.promise === promise) pending = null;
    if (script) script.remove(); // only the tag this attempt added
  });
  return promise;
}

/** Starts loading the script early (v3 scores better having seen the page). Never throws. */
export function preloadRecaptcha(siteKey = config.recaptchaSiteKey) {
  if (!siteKey || typeof document === 'undefined') return;
  loadGrecaptcha(siteKey).catch(() => {
    // Reported by getRecaptchaToken when a token is actually needed.
  });
}

/**
 * A fresh reCAPTCHA v3 token for `action`, or `null` (see the module comment). Never rejects.
 */
export async function getRecaptchaToken(
  action,
  { siteKey = config.recaptchaSiteKey, timeoutMs = DEFAULT_TIMEOUT_MS } = {},
) {
  if (!siteKey) {
    warn('no site key configured');
    return null;
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  const attempt = (async () => {
    const grecaptcha = await loadGrecaptcha(siteKey);
    if (!grecaptcha || typeof grecaptcha.execute !== 'function') {
      throw new Error('execute_unavailable');
    }
    try {
      return await grecaptcha.execute(siteKey, { action });
    } catch {
      throw new Error('execute_failed');
    }
  })();

  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
  });

  try {
    const token = await Promise.race([attempt, timeout]);
    if (token === TIMED_OUT) {
      warn('timeout');
      return null;
    }
    if (typeof token !== 'string' || !token) {
      warn('empty_token');
      return null;
    }
    return token;
  } catch (err) {
    warn(err && typeof err.message === 'string' ? err.message : 'error');
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * `payload` plus `recaptchaToken` when a token could be had; otherwise a copy without it.
 * Never rejects.
 */
export async function withRecaptchaToken(payload, action, options) {
  const recaptchaToken = await getRecaptchaToken(action, options);
  return recaptchaToken ? { ...payload, recaptchaToken } : { ...payload };
}

/** Test-only: forget the loaded script. */
export function _resetRecaptchaForTests() {
  pending = null;
  if (typeof document !== 'undefined') removeScriptTag();
  if (typeof window !== 'undefined') delete window.grecaptcha;
}
